import type { DatadogRum, RumInitConfiguration } from "@datadog/browser-rum";
import { scheduleBackgroundTask } from "./scheduleBackgroundTask";

type DatadogView = {
  hash?: string;
  name: string;
  pathname: string;
  search?: string;
};

const DATADOG_SERVICE = import.meta.env.VITE_DD_SERVICE || "admin-console-frontend";
const DATADOG_VERSION = import.meta.env.VITE_DD_VERSION;
const DATADOG_ENABLED = Boolean(import.meta.env.VITE_DD_APPLICATION_ID && import.meta.env.VITE_DD_CLIENT_TOKEN);

let bootstrapScheduled = false;
let datadogRumInstance: DatadogRum | undefined;
let datadogRumPromise: Promise<DatadogRum | undefined> | undefined;
let pendingView: DatadogView | undefined;
let lastTrackedViewKey: string | undefined;

function parseBoolean(value: string | undefined, fallback: boolean) {
  if (value === undefined) {
    return fallback;
  }

  return value === "true";
}

function parseNumber(value: string | undefined, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getViewKey(view: DatadogView) {
  return `${view.pathname}${view.search || ""}${view.hash || ""}`;
}

function flushPendingView() {
  if (!datadogRumInstance || !pendingView) {
    return;
  }

  const viewKey = getViewKey(pendingView);
  if (viewKey === lastTrackedViewKey) {
    pendingView = undefined;
    return;
  }

  datadogRumInstance.startView({
    name: pendingView.name,
    service: DATADOG_SERVICE,
    version: DATADOG_VERSION
  });
  lastTrackedViewKey = viewKey;
  pendingView = undefined;
}

async function loadDatadogRum() {
  if (!DATADOG_ENABLED) {
    return undefined;
  }

  if (!datadogRumPromise) {
    datadogRumPromise = import("@datadog/browser-rum")
      .then(({ datadogRum }) => {
        const rumConfig: RumInitConfiguration = {
          applicationId: import.meta.env.VITE_DD_APPLICATION_ID!,
          clientToken: import.meta.env.VITE_DD_CLIENT_TOKEN!,
          site: import.meta.env.VITE_DD_SITE || "datadoghq.com",
          service: DATADOG_SERVICE,
          env: import.meta.env.VITE_DD_ENV || import.meta.env.MODE,
          version: DATADOG_VERSION,
          sessionSampleRate: parseNumber(import.meta.env.VITE_DD_SESSION_SAMPLE_RATE, 100),
          sessionReplaySampleRate: parseBoolean(import.meta.env.VITE_ENABLE_SESSION_REPLAY, false) ? parseNumber(import.meta.env.VITE_DD_SESSION_REPLAY_SAMPLE_RATE, 20) : 0,
          trackResources: parseBoolean(import.meta.env.VITE_DD_TRACK_RESOURCES, true),
          trackLongTasks: parseBoolean(import.meta.env.VITE_DD_TRACK_LONG_TASKS, true),
          trackUserInteractions: parseBoolean(import.meta.env.VITE_DD_TRACK_USER_INTERACTIONS, true),
          trackViewsManually: true,
          defaultPrivacyLevel: "mask-user-input"
        };

        datadogRum.init(rumConfig);
        datadogRumInstance = datadogRum;
        flushPendingView();
        return datadogRum;
      })
      .catch((error) => {
        console.error("Failed to initialize Datadog RUM", error);
        return undefined;
      });
  }

  return datadogRumPromise;
}

export function bootstrapDatadogRum() {
  if (!DATADOG_ENABLED || bootstrapScheduled) {
    return;
  }

  bootstrapScheduled = true;
  scheduleBackgroundTask(
    () => {
      void loadDatadogRum();
    },
    { delay: 1000 }
  );
}

export function trackDatadogView(view: DatadogView) {
  if (!DATADOG_ENABLED) {
    return;
  }

  pendingView = view;

  if (datadogRumInstance) {
    flushPendingView();
  }
}
